/**
 * KAN-804: цвет балла — светофор из макета (эпик редизайна итогов).
 *
 * Лежит в `shared/`, а не рядом с виджетом итогов, потому что цвет балла нужен
 * больше чем одному виджету: страница итогов, инструменты аналитики (KAN-839),
 * и любая будущая витрина, показывающая ту же оценку. `ai/module-graph.md`
 * запрещает импорт виджета из виджета — общая логика выносится в `shared/`.
 *
 * Балл 0–100 показывают шесть секций страницы: кольца в пьедестале и таблице,
 * полосы в разложении MVP, оси профиля, столбики по играм. Если каждая покрасит
 * его по-своему, одна и та же цифра будет выглядеть по-разному в двух блоках, и
 * читатель решит, что это разные величины. Отсюда одна функция на всех.
 *
 * ШКАЛА ПРИВЯЗАНА К РЕАЛЬНЫМ БАЛЛАМ, А НЕ К ОТРЕЗКУ 0–100. Перцентильные оценки
 * почти не бывают у краёв: чтобы получить ноль, надо оказаться последним среди
 * сравнимых по каждой метрике. Поэтому красный берётся к 30, жёлтый приходится на
 * ~62, зелёный — к 95. Растяни мы палитру ровно по 0–100, вся выборка сплющилась бы
 * в жёлто-зелёную кашу, где 51 и 86 неразличимы.
 *
 * Зелёного на пути от красного к жёлтому нет намеренно: иначе слабая оценка на
 * промежуточном оттенке читалась бы как «хорошо».
 */

/** Нормализованное положение балла на шкале, 0..1. */
function ramp(value: number): number {
  const clamped = Math.max(0, Math.min(100, value));
  return Math.max(0, Math.min(1, (clamped - 30) / 65));
}

/**
 * Цвет балла. oklch, а не hex: по нему оттенок движется равномерно для глаза, и
 * середина шкалы не проваливается в грязь, как это делает линейный hsl.
 */
export function scoreColor(value: number): string {
  const t = ramp(value);
  const hue = Math.round(25 + t * 120);
  const lightness = (0.7 + (1 - Math.abs(t - 0.5) * 2) * 0.12).toFixed(3);
  const chroma = (0.155 + t * 0.035).toFixed(3);
  return `oklch(${lightness} ${chroma} ${hue})`;
}

/**
 * Тот же светофор заливкой: сверху светлее, снизу глубже. Для столбиков «оценка по
 * играм», где плоская заливка выглядит бумажной.
 */
export function scoreGradient(value: number): string {
  const t = ramp(value);
  const hue = Math.round(25 + t * 120);
  const lightness = 0.7 + (1 - Math.abs(t - 0.5) * 2) * 0.12;
  const chroma = 0.155 + t * 0.035;

  const top = `oklch(${(lightness + 0.09).toFixed(3)} ${(chroma * 0.85).toFixed(3)} ${hue})`;
  const mid = `oklch(${lightness.toFixed(3)} ${chroma.toFixed(3)} ${hue})`;
  const bottom = `oklch(${(lightness - 0.13).toFixed(3)} ${(chroma * 0.9).toFixed(3)} ${hue})`;

  return `linear-gradient(180deg, ${top} 0%, ${mid} 46%, ${bottom} 100%)`;
}
